/*
 * Bluo — Bundle-aware "Add to cart" + mini cart (Cowork)
 * ---------------------------------------------------------------------------
 * Problem this solves:
 *   The Pumper Bundles quantity-break widget does NOT write the selected
 *   bundle quantity into the product form's hidden `quantity` input. It keeps
 *   the real payload in `window.pmpr_cart_item` and exposes its own add
 *   helper, `window.prvw_custom_cart_add()`.
 *   Because of that, a plain native form submit from the "Add to cart" button
 *   always posted quantity=1 — one pair of glasses, no bundle discount.
 *
 * Fix:
 *   Intercept the click on the section's "Add to cart" button and hand the
 *   request to Pumper's own add helper, so the cart receives the exact bundle
 *   (correct quantity + __pumper_bundles line-item property) that "Buy now"
 *   already produces. Then refresh the header count and slide open a mini
 *   cart so the customer stays on the page.
 *
 * The mini cart has +/- unit steppers. Quantity changes go through
 * /cart/change.js and the panel repaints from the response, so the per-unit
 * price, line total and subtotal always reflect what Shopify actually charges
 * (including any bundle tier the new quantity qualifies for).
 *
 * Falls back to the normal form submit if Pumper is not loaded on the page.
 */
(function () {
  'use strict';

  var ATC_SELECTOR = 'form.cb__form button[name="add"], [data-bluo-bundle-atc]';
  var COUNT_SELECTOR = '.bhd-count, [data-cart-count], .cart-count-bubble span';

  /* ----------------------------------------------------------------- utils */

  function closestMatch(node, selector) {
    while (node && node.nodeType === 1) {
      if (node.matches && node.matches(selector)) return node;
      node = node.parentElement;
    }
    return null;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function money(cents, currency) {
    var amount = (cents || 0) / 100;
    var formatted = amount.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2
    });
    return (currency === 'PKR' ? 'Rs.' : (currency || '') + ' ') + formatted;
  }

  function unitPrice(item) {
    return item.final_price != null ? item.final_price : item.price;
  }

  function linePrice(item) {
    return item.final_line_price != null ? item.final_line_price : item.line_price;
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function updateCartCount(cart) {
    var nodes = document.querySelectorAll(COUNT_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = cart.item_count;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
  }

  function setLoading(button, on) {
    var label = button.querySelector('.cb__cta-label');

    if (on) {
      if (label && !button.dataset.bluoIdleLabel) button.dataset.bluoIdleLabel = label.textContent;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      if (label && button.dataset.loadingLabel) label.textContent = button.dataset.loadingLabel;
    } else {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      if (label && button.dataset.bluoIdleLabel) label.textContent = button.dataset.bluoIdleLabel;
    }
  }

  /* ------------------------------------------------------------- mini cart */

  var STYLES = [
    '.bluo-mc-scrim{position:fixed;inset:0;background:rgba(15,12,10,.44);opacity:0;pointer-events:none;transition:opacity .3s ease;z-index:99998}',
    '.bluo-mc-scrim.is-open{opacity:1;pointer-events:auto}',
    '.bluo-mc{position:fixed;top:0;right:0;bottom:0;width:min(400px,92vw);background:#fff;color:#111;z-index:99999;display:flex;flex-direction:column;transform:translateX(102%);transition:transform .38s cubic-bezier(.2,.7,.18,1);box-shadow:-18px 0 46px rgba(0,0,0,.18);font-family:inherit}',
    '.bluo-mc.is-open{transform:translateX(0)}',
    '.bluo-mc__head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:20px 22px;border-bottom:1px solid rgba(0,0,0,.1)}',
    '.bluo-mc__title{margin:0;font-size:13px;font-weight:700;letter-spacing:.16em;text-transform:uppercase}',
    '.bluo-mc__close{border:0;background:none;font-size:26px;line-height:1;cursor:pointer;padding:0 4px;color:inherit}',
    '.bluo-mc__body{flex:1;overflow-y:auto;padding:12px 22px;transition:opacity .2s ease}',
    '.bluo-mc__body[data-busy="true"]{opacity:.5;pointer-events:none}',
    '.bluo-mc__line{display:flex;gap:14px;padding:16px 0;border-bottom:1px solid rgba(0,0,0,.07)}',
    '.bluo-mc__img{width:62px;height:62px;object-fit:cover;background:#f4f3f1;flex:none}',
    '.bluo-mc__info{min-width:0;flex:1}',
    '.bluo-mc__name{margin:0 0 4px;font-size:13.5px;font-weight:600;line-height:1.3}',
    '.bluo-mc__meta{margin:0 0 10px;font-size:12px;color:#6b6b6b}',
    '.bluo-mc__price{font-size:13.5px;font-weight:700;white-space:nowrap}',
    '.bluo-mc__qty{display:flex;align-items:center;gap:0}',
    '.bluo-mc__step{width:30px;height:30px;border:1px solid rgba(0,0,0,.22);background:#fff;color:#111;font-size:15px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;font-family:inherit;padding:0}',
    '.bluo-mc__step:hover{background:#111;color:#fff;border-color:#111}',
    '.bluo-mc__step[disabled]{opacity:.35;cursor:not-allowed;background:#fff;color:#111;border-color:rgba(0,0,0,.22)}',
    '.bluo-mc__qtyval{min-width:38px;height:30px;border-top:1px solid rgba(0,0,0,.22);border-bottom:1px solid rgba(0,0,0,.22);display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:600;font-variant-numeric:tabular-nums}',
    '.bluo-mc__remove{margin-left:14px;border:0;background:none;padding:0;font-size:11.5px;color:#6b6b6b;text-decoration:underline;cursor:pointer;font-family:inherit}',
    '.bluo-mc__remove:hover{color:#111}',
    '.bluo-mc__foot{padding:18px 22px 22px;border-top:1px solid rgba(0,0,0,.1);display:grid;gap:10px}',
    '.bluo-mc__sub{display:flex;justify-content:space-between;font-size:14px;font-weight:700;margin-bottom:4px}',
    '.bluo-mc__btn{display:flex;align-items:center;justify-content:center;padding:15px 20px;font-size:14px;font-weight:600;letter-spacing:.03em;text-decoration:none;border:1px solid #111;cursor:pointer;background:#111;color:#fff;font-family:inherit}',
    '.bluo-mc__btn--ghost{background:transparent;color:#111}',
    '.bluo-mc__empty{padding:40px 0;text-align:center;color:#6b6b6b;font-size:14px}',
    '@media (prefers-reduced-motion: reduce){.bluo-mc,.bluo-mc-scrim{transition:none}}'
  ].join('');

  var refs = null;

  function buildMiniCart() {
    if (refs) return refs;

    var style = document.createElement('style');
    style.textContent = STYLES;
    document.head.appendChild(style);

    var scrim = document.createElement('div');
    scrim.className = 'bluo-mc-scrim';

    var panel = document.createElement('aside');
    panel.className = 'bluo-mc';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', 'Cart');
    panel.innerHTML =
      '<div class="bluo-mc__head">' +
        '<h2 class="bluo-mc__title">Added to cart</h2>' +
        '<button type="button" class="bluo-mc__close" aria-label="Close cart">&times;</button>' +
      '</div>' +
      '<div class="bluo-mc__body"></div>' +
      '<div class="bluo-mc__foot">' +
        '<div class="bluo-mc__sub"><span>Subtotal</span><span data-sub></span></div>' +
        '<a class="bluo-mc__btn" href="/checkout">Checkout</a>' +
        '<a class="bluo-mc__btn bluo-mc__btn--ghost" href="/cart">View cart</a>' +
      '</div>';

    document.body.appendChild(scrim);
    document.body.appendChild(panel);

    refs = {
      scrim: scrim,
      panel: panel,
      body: panel.querySelector('.bluo-mc__body'),
      sub: panel.querySelector('[data-sub]')
    };

    scrim.addEventListener('click', closeMiniCart);
    panel.querySelector('.bluo-mc__close').addEventListener('click', closeMiniCart);
    refs.body.addEventListener('click', handleQuantityClick);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeMiniCart();
    });

    return refs;
  }

  function closeMiniCart() {
    if (!refs) return;
    refs.panel.classList.remove('is-open');
    refs.scrim.classList.remove('is-open');
  }

  function paint(cart) {
    var r = buildMiniCart();

    if (!cart.items.length) {
      r.body.innerHTML = '<p class="bluo-mc__empty">Your cart is empty.</p>';
    } else {
      r.body.innerHTML = cart.items.map(function (item, index) {
        var img = item.image
          ? '<img class="bluo-mc__img" src="' + item.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2') + '" alt="" loading="lazy">'
          : '<span class="bluo-mc__img"></span>';

        return '<div class="bluo-mc__line" data-line="' + (index + 1) + '">' + img +
          '<div class="bluo-mc__info">' +
            '<p class="bluo-mc__name">' + escapeHtml(item.product_title) + '</p>' +
            '<p class="bluo-mc__meta">' + money(unitPrice(item), cart.currency) + ' each</p>' +
            '<div class="bluo-mc__qty">' +
              '<button type="button" class="bluo-mc__step" data-step="-1" aria-label="Decrease quantity"' +
                (item.quantity <= 1 ? ' disabled' : '') + '>&minus;</button>' +
              '<span class="bluo-mc__qtyval" data-qty>' + item.quantity + '</span>' +
              '<button type="button" class="bluo-mc__step" data-step="1" aria-label="Increase quantity">+</button>' +
              '<button type="button" class="bluo-mc__remove" data-remove>Remove</button>' +
            '</div>' +
          '</div>' +
          '<span class="bluo-mc__price">' + money(linePrice(item), cart.currency) + '</span>' +
        '</div>';
      }).join('');
    }

    r.sub.textContent = money(
      cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price,
      cart.currency
    );
  }

  function openMiniCart(cart) {
    var r = buildMiniCart();
    paint(cart);

    // Force a reflow rather than waiting on requestAnimationFrame: rAF is
    // throttled in background tabs, which would leave the panel off-screen.
    void r.panel.offsetHeight;
    r.scrim.classList.add('is-open');
    r.panel.classList.add('is-open');
  }

  function handleQuantityClick(event) {
    var control = closestMatch(event.target, '.bluo-mc__step, .bluo-mc__remove');
    if (!control || control.disabled) return;

    var lineEl = closestMatch(control, '[data-line]');
    if (!lineEl || !refs || refs.body.dataset.busy === 'true') return;

    var line = parseInt(lineEl.getAttribute('data-line'), 10);
    var current = parseInt(lineEl.querySelector('[data-qty]').textContent, 10) || 0;
    var next = control.hasAttribute('data-remove')
      ? 0
      : current + parseInt(control.getAttribute('data-step'), 10);

    if (next < 0) next = 0;
    if (next === current) return;

    refs.body.dataset.busy = 'true';

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ line: line, quantity: next })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateCartCount(cart);
        paint(cart);
      })
      .catch(function () { /* leave the panel as-is on failure */ })
      .then(function () {
        refs.body.dataset.busy = 'false';
      });
  }

  /* -------------------------------------------------------------- add flow */

  function fallbackAdd(form) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      body: new FormData(form)
    });
  }

  function handleClick(event) {
    var button = closestMatch(event.target, ATC_SELECTOR);
    if (!button || button.disabled) return;

    // Pumper not on the page (or not loaded yet) — leave the native submit alone.
    if (typeof window.prvw_custom_cart_add !== 'function') return;

    var form = button.form || closestMatch(button, 'form');

    event.preventDefault();
    event.stopPropagation();

    if (button.dataset.bluoBusy === 'true') return;
    button.dataset.bluoBusy = 'true';
    setLoading(button, true);

    Promise.resolve()
      .then(function () { return window.prvw_custom_cart_add(); })
      .catch(function () { return form ? fallbackAdd(form) : null; })
      .then(function () { return getCart(); })
      .then(function (cart) {
        updateCartCount(cart);
        openMiniCart(cart);
      })
      .catch(function () {
        // Last resort: send the shopper to the cart page so nothing is lost.
        window.location.href = '/cart';
      })
      .then(function () {
        setLoading(button, false);
        button.dataset.bluoBusy = 'false';
      });
  }

  // Capture phase so this runs before the form's submit handling.
  document.addEventListener('click', handleClick, true);
})();
